import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { products, productCategories, CATEGORY_IMG, type Product } from "@/mocks/products";
import ProductModal from "./ProductModal";

const catMap = Object.fromEntries(productCategories.filter((c) => c.slug !== "all").map((c) => [c.slug, c]));

export function ProductCard({ product, index, visible, onClick }: { product: Product; index: number; visible: boolean; onClick?: () => void }) {
  const cat = catMap[product.category] || { label: product.type, icon: "ri-cpu-line", color: "#3B82F6" };
  const title = product.desc || product.code;
  return (
    <div
      onClick={onClick}
      className={`group bg-white border border-[#E2E8F0] hover:border-[#3B82F6] rounded-xl overflow-hidden transition-all duration-500 hover:shadow-lg flex flex-col ${onClick ? "cursor-pointer" : ""} ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#F1F5F9]">
        <img src={CATEGORY_IMG[product.category] || CATEGORY_IMG.other} alt={product.type} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
        <span className="absolute top-3 right-3 bg-[#0F172A] text-white text-xs font-bold px-2.5 py-1 rounded shadow">{product.brand}</span>
        <span className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-white/90 backdrop-blur px-2.5 py-1 rounded text-xs font-semibold" style={{ color: cat.color }}><i className={cat.icon}></i>{cat.label}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs font-semibold tracking-widest uppercase mb-1.5" style={{ color: cat.color }}>{product.type}</p>
        <h3 className="text-[#0F172A] font-bold text-sm leading-snug mb-3 line-clamp-2 min-h-[2.5rem]">{title}</h3>
        <div className="space-y-1 mt-auto">
          <div className="flex items-center gap-1.5 text-[#475569] text-xs"><i className="ri-barcode-line text-[#94A3B8]"></i><span className="truncate font-medium">{product.code}</span></div>
          {product.dmo && <div className="flex items-center gap-1.5 text-[#64748B] text-xs"><i className="ri-government-line text-[#94A3B8]"></i><span>DMO Ürün Kodu: {product.dmo}</span></div>}
        </div>
      </div>
    </div>
  );
}

export default function ProductsPreview() {
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState<Product | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const featured = products.filter((p) => p.featured).slice(0, 4);
  const list = featured.length >= 4 ? featured : products.slice(0, 4);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-[#F1F5F9] py-24 border-t border-[#E2E8F0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div>
            <div className="flex items-center gap-3 mb-4"><div className="h-px w-12 bg-[#3B82F6]"></div><span className="text-[#3B82F6] text-xs font-semibold tracking-[0.3em] uppercase">Ürün Kataloğu</span></div>
            <h2 className="text-[#0F172A] font-black uppercase leading-tight" style={{ fontSize: "clamp(20px, 2.8vw, 34px)", letterSpacing: "-0.02em" }}>Öne Çıkan <span className="text-[#64748B]">Ürünlerimiz</span></h2>
          </div>
          <Link to="/projelerimiz" className="whitespace-nowrap flex items-center gap-2 border border-[#E2E8F0] text-[#475569] hover:border-[#3B82F6] hover:text-[#3B82F6] px-6 py-3 text-sm font-semibold tracking-widest uppercase transition-all duration-300 cursor-pointer self-start md:self-auto bg-white">
            Tüm Ürünler <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-base"></i></div>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {list.map((p, i) => <ProductCard key={p.id} product={p} index={i} visible={visible} onClick={() => setSelected(p)} />)}
        </div>
      </div>
      {selected && <ProductModal product={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
